import { useMemo, useState } from 'react';

import type { BadgeTone } from '@/components/admin/Badge';
import { CaseReportSidePanel } from '@/features/sessions/editor/CaseReportSidePanel';
import {
  type AutosaveState,
  ClinicalEditorShell,
  type EditorDocument,
} from '@/features/sessions/editor/ClinicalEditorShell';
import { EditorFooter } from '@/features/sessions/editor/EditorFooter';
import { EditorTopbar } from '@/features/sessions/editor/EditorTopbar';
import { hasPermission } from '@/lib/rbac/hasPermission';
import { clinicalBreadcrumb } from '@/lib/telemetry/clinical';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';

import { aiDraftToEditorContent } from '../editor/aiDraftToEditorContent';
import {
  useApproveCaseReport,
  useCreateCaseReport,
  useRejectCaseReport,
  useSubmitCaseReport,
  useUpdateCaseReport,
} from '../hooks';
import { canCreateCaseReport, canEditCaseReport } from '../lib/artifactPermissions';
import type { CaseReport, CaseReportStatus } from '../types/schemas';

import { AiDraftBanner } from './journal/AiDraftBanner';
import { SealRitualDialog } from './journal/SealRitualDialog';

interface Props {
  sessionId: string;
  /** Id of the practitioner who handled this session. Gates "new" mode
   *  authoring to those the BE lets create a report for this session — mirrors
   *  the create guard so a non-owner reaching the route by URL can't start one. */
  practitionerId: string;
  // Null on anonymous helpline sessions; the back-link then omits the client.
  clientId: string | null;
  clientName: string;
  scheduledStart: string | null;
  authorName: string;
  /** null when in "new" mode (route mounts editor before any POST). */
  report: CaseReport | null;
  /** Fired once after the first POST creates the report. Only required for "new" mode. */
  onCreated?: (report: CaseReport) => void;
  onSubmitted: (report: CaseReport) => void;
  /** Fired after a reviewer approves or rejects (admin / clinical surfaces). */
  onReviewed?: (report: CaseReport) => void;
}

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

const STATUS_LABEL: Record<CaseReportStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_TONE: Record<CaseReportStatus, BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'success',
  rejected: 'danger',
};

interface ReportTopbarProps {
  sessionId: string;
  // Null on anonymous helpline sessions; the back-link then omits the client.
  clientId: string | null;
  clientName: string;
  scheduledStart: string | null;
  authorName: string;
  status: CaseReportStatus;
  statusLabel: string;
  statusTone: BadgeTone;
  autosaveState: AutosaveState;
  wordCount: number;
  primaryAction: PrimaryAction | undefined;
  showAiBanner: boolean;
}

function ReportTopbar({
  sessionId,
  clientId,
  clientName,
  scheduledStart,
  authorName,
  status,
  statusLabel,
  statusTone,
  autosaveState,
  wordCount,
  primaryAction,
  showAiBanner,
}: ReportTopbarProps) {
  return (
    <div className="flex flex-col">
      <EditorTopbar
        kind="case-report"
        clientName={clientName}
        scheduledStart={scheduledStart}
        status={status}
        statusTone={statusTone}
        statusLabel={statusLabel}
        authorLabel={authorName}
        autosaveState={autosaveState}
        wordCount={wordCount}
        primaryAction={primaryAction}
        backTo={{ sessionId, clientId, drawerTab: 'case-reports' }}
      />
      {showAiBanner ? (
        <div className="border-accent-ink/10 border-b bg-white px-4 py-2.5">
          <AiDraftBanner showing={showAiBanner} />
        </div>
      ) : null}
    </div>
  );
}

interface ReportFooterProps {
  autosaveState: AutosaveState;
  wordCount: number;
  characterCount: number;
  focusMode: boolean;
  onToggleFocus: () => void;
  primaryAction: PrimaryAction | undefined;
}

function ReportFooter({
  autosaveState,
  wordCount,
  characterCount,
  focusMode,
  onToggleFocus,
  primaryAction,
}: ReportFooterProps) {
  return (
    <EditorFooter
      autosaveState={autosaveState}
      wordCount={wordCount}
      characterCount={characterCount}
      focusMode={focusMode}
      onToggleFocus={onToggleFocus}
      primaryAction={primaryAction}
    />
  );
}

/**
 * Case report editor — D10 rewrite on top of `ClinicalEditorShell`. The
 * shell owns the prose surface, autosave debounce, focus mode, bubble
 * toolbar and shortcut overlay; this component composes it with the
 * report-specific topbar / footer / side panel and wires in the
 * seal-ritual submit + reviewer approve/reject flows.
 *
 * Two modes:
 *   - report === null: "new" mode. Route mounts editor before any POST;
 *     the first autosave creates the row and fires `onCreated`.
 *   - report !== null: existing report. Subsequent autosaves PATCH it.
 *
 * Status flow: draft → submitted → approved | rejected. The editor is
 * read-only for any status other than 'draft' (rejected reverts to draft
 * on the BE when the author edits it; that round-trip is handled by the
 * mutation, not the FE). Reviewer card is shown on the side panel when
 * the user has `case_report:review` AND is not the author AND status is
 * 'submitted' (matches D9 visibility rule).
 *
 * Telemetry (`clinicalBreadcrumb`) and toast helpers (`reportSubmitted`,
 * `reportApproved`, `reportRejected`, etc.) are preserved from the
 * pre-rewrite editor.
 */
export function CaseReportEditor(props: Props) {
  // One instance per document. A pending edit flushes on switch, and a shared
  // instance would rebind that in-flight save to the next document.
  return (
    <CaseReportDocumentEditor key={`${props.sessionId}:${props.report?.id ?? 'new'}`} {...props} />
  );
}

function CaseReportDocumentEditor({
  sessionId,
  practitionerId,
  clientId,
  clientName,
  scheduledStart,
  authorName,
  report,
  onCreated,
  onSubmitted,
  onReviewed,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const userId = user?.id ?? null;

  // Authorship-aware edit gate, from the shared artifactPermissions source of
  // truth. Permission alone is not enough — chiefs/leads hold
  // `case_report:update` for their own drafts, not for colleagues'. In "new"
  // mode (report === null) the BE CREATE rule decides who may author (session
  // practitioner or an elevated role); for an existing report the stricter BE
  // EDIT rule applies (author-only, super_admin excepted).
  const isSuperAdmin = role === 'super_admin';
  const canAuthorThisReport =
    report === null
      ? canCreateCaseReport(user, practitionerId)
      : canEditCaseReport(user, report.authorId);
  const canEdit =
    role !== null &&
    (hasPermission(role, 'case_report:create') || hasPermission(role, 'case_report:update')) &&
    canAuthorThisReport;
  const canReview = role !== null && hasPermission(role, 'case_report:review');

  const create = useCreateCaseReport(sessionId);
  const update = useUpdateCaseReport(sessionId, report?.id ?? '');
  const submit = useSubmitCaseReport(sessionId);
  const approve = useApproveCaseReport(sessionId);
  const reject = useRejectCaseReport(sessionId);

  const [sealOpen, setSealOpen] = useState(false);
  // Mirror the pre-rewrite "AI-drafted" auto-dismiss-on-edit behaviour: the
  // banner appears when the report was drafted by AI and disappears the
  // moment the practitioner's keystrokes flow through autosave.
  const [proseEditedSinceArrival, setProseEditedSinceArrival] = useState(false);

  const status: CaseReportStatus = report?.status ?? 'draft';
  // Editing is allowed on draft AND rejected reports (or when no report
  // exists yet — "new" mode). The BE update endpoint accepts both states
  // and silently resets `rejected → draft` on save, which is the only
  // path back from a rejection — locking the field would strand the
  // author. Submitted / approved remain read-only.
  const isReadOnly = !canEdit || (report !== null && status !== 'draft' && status !== 'rejected');

  const onAutosave = async (content: string): Promise<void> => {
    setProseEditedSinceArrival(true);
    try {
      if (report === null) {
        const created = await create.mutateAsync({ content });
        clinicalBreadcrumb('case-report:create', { sessionId });
        onCreated?.(created);
        return;
      }
      await update.mutateAsync({ content });
      clinicalBreadcrumb('case-report:update', { sessionId, reportId: report.id });
    } catch (err) {
      toast.saveFailed();
      throw err;
    }
  };

  const onSubmitClick = (): void => {
    if (report === null) {
      toast.error('Save the report before submitting.');
      return;
    }
    setSealOpen(true);
  };

  const onSealConfirm = async (): Promise<void> => {
    if (report === null) return;
    try {
      const submitted = await submit.mutateAsync(report.id);
      clinicalBreadcrumb('case-report:submit', { sessionId, reportId: report.id });
      toast.reportSubmitted();
      setSealOpen(false);
      onSubmitted(submitted);
    } catch {
      toast.reportSubmitFailed();
    }
  };

  const onApprove = (): void => {
    if (report === null) return;
    void approve
      .mutateAsync(report.id)
      .then((updated) => {
        clinicalBreadcrumb('case-report:approve', { sessionId, reportId: report.id });
        toast.reportApproved();
        onReviewed?.(updated);
      })
      .catch(() => {
        toast.approveFailed();
      });
  };

  const onReject = (reason: string): void => {
    if (report === null) return;
    void reject
      .mutateAsync({ reportId: report.id, rejectionReason: reason })
      .then((updated) => {
        clinicalBreadcrumb('case-report:reject', { sessionId, reportId: report.id });
        toast.reportRejected();
        onReviewed?.(updated);
      })
      .catch(() => {
        toast.rejectFailed();
      });
  };

  const document = useMemo<EditorDocument | null>(() => {
    if (report === null) return null;
    return {
      id: report.id,
      // CaseReport.content is `.nullish()` so it can be `undefined`; the
      // shell contract expects `string | null` — narrow at the boundary.
      // AI-drafted reports persist NDJSON; convert to HTML on read so the
      // editor receives Tiptap-parseable markup. The converter is
      // idempotent — once the first save replaces content with HTML, this
      // is a no-op pass-through.
      content: aiDraftToEditorContent(report.content),
      status: report.status,
      updatedAt: report.updatedAt ?? null,
    };
  }, [report]);

  const statusLabel = STATUS_LABEL[status];
  const statusTone = STATUS_TONE[status];

  // Primary action visible for draft AND rejected reports the user can
  // edit. Suppressed in "new" mode (no report yet) — autosave creates
  // the row first, then the action appears on the next render once
  // `report` lands. For rejected reports the BE flips status back to
  // draft on save, so by the time the author submits the badge already
  // reads "Draft" and routing re-runs cleanly.
  const primaryAction: PrimaryAction | undefined =
    canEdit && report !== null && (status === 'draft' || status === 'rejected')
      ? {
          label: status === 'rejected' ? 'Resubmit for review' : 'Submit for review',
          onClick: onSubmitClick,
          disabled: submit.isPending,
        }
      : undefined;

  const showAiBanner =
    !isReadOnly &&
    report?.transcriptJobId !== null &&
    report?.transcriptJobId !== undefined &&
    !proseEditedSinceArrival;

  // Reviewer card shows when the user can plausibly act on this report:
  //   - the actively assigned reviewer (lead path), or
  //   - a chief seeing a chief-level pending orphan (reviewerId=null), or
  //   - super_admin override.
  // APPROXIMATES the BE gate: the orphan branch checks role only. The BE
  // additionally requires an active assignment at the session's location
  // (isChiefAtLocation), which the FE payload can't derive. Via the review
  // queue only eligible reports surface; a chief deep-linking to a report at
  // a location they're not assigned to will see buttons that 403 on click.
  const isAssignedReviewer = userId !== null && report?.reviewerId === userId;
  const isChiefOrphanPickup = report?.reviewerId == null && role === 'chief_psychologist';
  const showReviewer =
    canReview &&
    report !== null &&
    status === 'submitted' &&
    report.authorId !== userId &&
    (isAssignedReviewer || isChiefOrphanPickup || isSuperAdmin);

  return (
    <>
      {/* `topbar` and `footer` are intentional render-prop slots on the shell
          (they receive shell ctx — autosave state, word count, focus toggle).
          The rule misreads them as inline component definitions; we hand off
          to stable top-level components (ReportTopbar / ReportFooter) so React
          reconciliation is not actually impacted. */}
      <ClinicalEditorShell
        kind="case-report"
        document={document}
        isReadOnly={isReadOnly}
        onAutosave={onAutosave}
        documentMeta={{ clientName, scheduledStart, authorLabel: authorName }}
        // eslint-disable-next-line react/no-unstable-nested-components
        topbar={(ctx) => (
          <ReportTopbar
            sessionId={sessionId}
            clientId={clientId}
            clientName={clientName}
            scheduledStart={scheduledStart}
            authorName={authorName}
            status={status}
            statusLabel={statusLabel}
            statusTone={statusTone}
            autosaveState={ctx.autosaveState}
            wordCount={ctx.wordCount}
            primaryAction={primaryAction}
            showAiBanner={showAiBanner}
          />
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        footer={(ctx) => (
          <ReportFooter
            autosaveState={ctx.autosaveState}
            wordCount={ctx.wordCount}
            characterCount={ctx.characterCount}
            focusMode={ctx.focusMode}
            onToggleFocus={ctx.toggleFocusMode}
            primaryAction={primaryAction}
          />
        )}
        sidePanel={
          report !== null ? (
            <CaseReportSidePanel
              report={report}
              showReviewer={showReviewer}
              currentUserId={userId}
              onApprove={onApprove}
              onReject={onReject}
              isApproving={approve.isPending}
              isRejecting={reject.isPending}
            />
          ) : (
            <p className="text-xs text-neutral-500">
              The report will appear here once you start writing.
            </p>
          )
        }
      />
      {sealOpen ? (
        <SealRitualDialog
          open={sealOpen}
          onOpenChange={setSealOpen}
          kind="report"
          onConfirm={() => {
            void onSealConfirm();
          }}
          isPending={submit.isPending || update.isPending}
        />
      ) : null}
    </>
  );
}
